import { useState, useMemo, useEffect } from 'react';
import { backdropClose } from '../utils/backdropClose';
import { useQuery } from '@tanstack/react-query';
import { useApi } from '../utils/useApi';
import { useUserContext } from '../contexts/UserContext';
import { Calendar, UserX, IndianRupee, Download, AlertTriangle, X, Check, FileDown, RefreshCw, Phone, Filter, Receipt, FileSpreadsheet, Award, ChevronDown, ChevronRight } from 'lucide-react';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import NumberInput from '../components/ui/NumberInput';
import toast from 'react-hot-toast';

type Tab = 'expiry' | 'inactive' | 'revenue' | 'sales' | 'trainer' | 'download';

const TABS: { id: Tab; label: string; icon: React.ReactNode; description: string }[] = [
  { id: 'expiry', label: 'Expiry Forecast', icon: <Calendar className="w-4 h-4" />, description: 'Members whose membership ends soon' },
  { id: 'inactive', label: 'Not Visiting', icon: <UserX className="w-4 h-4" />, description: 'Members with no recent check-in' },
  { id: 'revenue', label: 'Revenue Collection', icon: <IndianRupee className="w-4 h-4" />, description: 'Pending & overdue payments' },
  { id: 'sales', label: 'Sale Report', icon: <Receipt className="w-4 h-4" />, description: 'Sales within a date range — by invoice or line item' },
  { id: 'trainer', label: 'Trainer Engagement', icon: <Award className="w-4 h-4" />, description: 'Members assigned to each trainer with engagement % and check-ins (for incentive calc)' },
  { id: 'download', label: 'Download Members', icon: <Download className="w-4 h-4" />, description: 'Export member list as PDF' },
];

const EXPORT_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'phone', label: 'Phone' },
  { key: 'activePlan', label: 'Active Plan' },
  { key: 'status', label: 'Status' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: 'Gender' },
  { key: 'city', label: 'City' },
  { key: 'branch', label: 'Branch' },
  { key: 'joiningDate', label: 'Joining Date' },
  { key: 'planExpiry', label: 'Plan Expiry' },
];

const MAX_COLUMNS = 4;

function fmt(amount: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    inactive: 'bg-gray-50 text-gray-500 border-gray-200',
    lead: 'bg-blue-50 text-blue-700 border-blue-200',
    trial: 'bg-violet-50 text-violet-700 border-violet-200',
    due: 'bg-rose-50 text-rose-700 border-rose-200',
    partial: 'bg-amber-50 text-amber-700 border-amber-200',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${styles[status] || 'bg-gray-50 text-gray-500 border-gray-200'}`}>
      {status}
    </span>
  );
}

function DaysChip({ days, warn = 14 }: { days: number; warn?: number }) {
  const color = days <= 7 ? 'bg-rose-50 text-rose-700 border-rose-200' : days <= warn ? 'bg-amber-50 text-amber-700 border-amber-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold border ${color}`}>{days}d</span>;
}

// ── Expiry Forecast ──────────────────────────────────────────────────────────

function ExpiryForecast() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();
  const [days, setDays] = useState(30);

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['reports-expiry', userContext?.user?.id, days],
    async () => {
      const r = await fetchWithAuth(`/api/reports/expiry-forecast?days=${days}`);
      return r as { data: any[]; meta: { days: number; total: number } };
    },
    { enabled: !!userContext && !userLoading, staleTime: 2 * 60 * 1000 }
  );

  const rows = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p className="text-zinc-500 text-sm font-medium">{rows.length} member{rows.length !== 1 ? 's' : ''} expiring within <span className="font-bold text-zinc-800">{days} days</span></p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {[7, 14, 30, 60].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${days === d ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button onClick={() => refetch()} className="p-2 rounded-xl bg-gray-100 text-zinc-500 hover:bg-gray-200 transition-colors">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No memberships expiring in the next {days} days</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Member</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider hidden sm:table-cell">Plan</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Expires</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Left</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider hidden md:table-cell">Payment</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row: any, i: number) => (
                <tr key={row.memberId + i} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-bold text-zinc-900">{row.name}</p>
                    <p className="text-[11px] text-zinc-400 flex items-center gap-1"><Phone className="w-3 h-3" />{row.phone}</p>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell">
                    <span className="text-zinc-700 font-medium">{row.plan}</span>
                  </td>
                  <td className="px-4 py-3 text-zinc-600 font-medium">{row.endDate}</td>
                  <td className="px-4 py-3"><DaysChip days={row.daysLeft} /></td>
                  <td className="px-4 py-3 hidden md:table-cell"><StatusBadge status={row.paymentStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Not Visiting (no recent check-in) ─────────────────────────────────────────────────────────

function InactiveMembers() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();
  const [days, setDays] = useState(30);

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['reports-inactive', userContext?.user?.id, days],
    async () => {
      const r = await fetchWithAuth(`/api/reports/inactive-members?days=${days}`);
      return r as { data: any[]; meta: { days: number; total: number } };
    },
    { enabled: !!userContext && !userLoading, staleTime: 2 * 60 * 1000 }
  );

  const rows = data?.data || [];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <p className="text-zinc-500 text-sm font-medium">{rows.length} active member{rows.length !== 1 ? 's' : ''} with no check-in in <span className="font-bold text-zinc-800">{days}+ days</span></p>
        <div className="flex items-center gap-3">
          <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
            {[30, 60, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${days === d ? 'bg-white text-zinc-900 shadow-sm' : 'text-zinc-500 hover:text-zinc-700'}`}
              >
                {d}d
              </button>
            ))}
          </div>
          <button onClick={() => refetch()} className="p-2 rounded-xl bg-gray-100 text-zinc-500 hover:bg-gray-200 transition-colors">
            <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <UserX className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">All active members have checked in recently</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Member</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider hidden sm:table-cell">Active Plan</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Last Check-in</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider hidden md:table-cell">Since Last Visit</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row: any, i: number) => (
                <tr key={row.memberId + i} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-bold text-zinc-900">{row.name}</p>
                    <p className="text-[11px] text-zinc-400 flex items-center gap-1"><Phone className="w-3 h-3" />{row.phone}</p>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-zinc-600 font-medium">{row.activePlan}</td>
                  <td className="px-4 py-3">
                    {row.lastCheckin ? (
                      <span className="text-zinc-600">{row.lastCheckin}</span>
                    ) : (
                      <span className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">Never</span>
                    )}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {row.daysSinceCheckin !== null
                      ? <DaysChip days={row.daysSinceCheckin} warn={60} />
                      : <span className="text-[10px] font-bold text-rose-500 uppercase tracking-wider">—</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Revenue Collection ───────────────────────────────────────────────────────

function RevenueCollection() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['reports-revenue', userContext?.user?.id],
    async () => {
      const r = await fetchWithAuth('/api/reports/revenue-collection');
      return r as { data: any[]; summary: { total: number; totalPending: number; overdueCount: number } };
    },
    { enabled: !!userContext && !userLoading, staleTime: 2 * 60 * 1000 }
  );

  const rows = data?.data || [];
  const summary = data?.summary;

  return (
    <div className="space-y-6">
      {summary && summary.total > 0 && (
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
            <p className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1">Total Pending</p>
            <p className="text-2xl font-black text-zinc-900">{fmt(summary.totalPending)}</p>
          </div>
          <div className="bg-amber-50 rounded-2xl p-4 border border-amber-100">
            <p className="text-[10px] font-black text-amber-500 uppercase tracking-wider mb-1">Records</p>
            <p className="text-2xl font-black text-zinc-900">{summary.total}</p>
          </div>
          <div className="bg-rose-50 rounded-2xl p-4 border border-rose-100">
            <p className="text-[10px] font-black text-rose-500 uppercase tracking-wider mb-1">Overdue</p>
            <p className="text-2xl font-black text-zinc-900">{summary.overdueCount}</p>
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button onClick={() => refetch()} className="p-2 rounded-xl bg-gray-100 text-zinc-500 hover:bg-gray-200 transition-colors">
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-zinc-400">
          <IndianRupee className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-semibold">No pending payments — all clear!</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Member</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider hidden sm:table-cell">Plan</th>
                <th className="text-right px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Total</th>
                <th className="text-right px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Pending</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider hidden md:table-cell">Due Date</th>
                <th className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {rows.map((row: any, i: number) => (
                <tr key={row.membershipId + i} className={`hover:bg-gray-50/50 transition-colors ${row.isOverdue ? 'bg-rose-50/30' : ''}`}>
                  <td className="px-4 py-3">
                    <p className="font-bold text-zinc-900">{row.name}</p>
                    <p className="text-[11px] text-zinc-400 flex items-center gap-1"><Phone className="w-3 h-3" />{row.phone}</p>
                  </td>
                  <td className="px-4 py-3 hidden sm:table-cell text-zinc-600 font-medium">{row.plan}</td>
                  <td className="px-4 py-3 text-right text-zinc-600 font-medium">{fmt(row.totalAmount)}</td>
                  <td className="px-4 py-3 text-right font-bold text-rose-600">{fmt(row.pendingAmount)}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {row.dueDate ? (
                      <span className={row.isOverdue ? 'text-rose-600 font-bold' : 'text-zinc-600'}>{row.dueDate}</span>
                    ) : '—'}
                    {row.isOverdue && <span className="ml-1 text-[10px] font-black text-rose-500 uppercase tracking-wider">OVERDUE</span>}
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={row.paymentStatus} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Download Members ─────────────────────────────────────────────────────────

function DownloadMembers() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();
  const [selectedCols, setSelectedCols] = useState<string[]>(['name', 'phone', 'activePlan', 'status']);
  const [showPiiWarning, setShowPiiWarning] = useState(false);
  const [generating, setGenerating] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [branchId, setBranchId] = useState('');
  const [expiryFrom, setExpiryFrom] = useState('');
  const [expiryTo, setExpiryTo] = useState('');

  // Build query string from active filters
  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
    if (branchId) params.set('branchId', branchId);
    if (expiryFrom) params.set('expiryFrom', expiryFrom);
    if (expiryTo) params.set('expiryTo', expiryTo);
    const s = params.toString();
    return s ? `?${s}` : '';
  }, [statusFilter, branchId, expiryFrom, expiryTo]);

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['reports-members-export', userContext?.user?.id, queryString],
    async () => {
      const r = await fetchWithAuth(`/api/reports/members-export${queryString}`);
      return r as { data: any[]; meta: { total: number } };
    },
    { enabled: !!userContext && !userLoading, staleTime: 2 * 60 * 1000 }
  );

  // Fetch branches for filter dropdown
  const { data: branchesData } = useQuery(
    ['branches-list', userContext?.user?.id],
    async () => {
      const r = await fetchWithAuth('/api/branches');
      return r as { branches?: any[]; data?: any[] };
    },
    { enabled: !!userContext && !userLoading, staleTime: 10 * 60 * 1000 }
  );
  const branches: any[] = branchesData?.branches || branchesData?.data || [];

  const rows = data?.data || [];

  const toggleCol = (key: string) => {
    if (selectedCols.includes(key)) {
      setSelectedCols(prev => prev.filter(c => c !== key));
    } else {
      if (selectedCols.length >= MAX_COLUMNS) {
        toast.error(`Maximum ${MAX_COLUMNS} columns allowed`);
        return;
      }
      setSelectedCols(prev => [...prev, key]);
    }
  };

  const colLabel = (key: string) => EXPORT_COLUMNS.find(c => c.key === key)?.label || key;

  const generatePdf = async () => {
    if (selectedCols.length === 0) {
      toast.error('Select at least one column');
      return;
    }
    setGenerating(true);
    setShowPiiWarning(false);
    try {
      const { default: jsPDF } = await import('jspdf');

      const isLandscape = selectedCols.length >= 3;
      const doc = new jsPDF({ orientation: isLandscape ? 'landscape' : 'portrait', unit: 'mm', format: 'a4' });

      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentW = pageW - margin * 2;
      const colW = contentW / selectedCols.length;
      const rowH = 8;
      const headerH = 9;

      // Title
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(17, 17, 17);
      doc.text('Member List', margin, 16);

      // Meta line
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      const dateStr = new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
      doc.text(`${dateStr}  ·  ${rows.length} members  ·  Contains personal data — handle with care`, margin, 22);

      // Divider
      doc.setDrawColor(228, 228, 231);
      doc.line(margin, 25, pageW - margin, 25);

      let y = 33;

      const drawHeader = () => {
        // Header background
        doc.setFillColor(244, 244, 245);
        doc.rect(margin, y - headerH + 2, contentW, headerH, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(80, 80, 80);
        selectedCols.forEach((c, i) => {
          doc.text(colLabel(c).toUpperCase(), margin + i * colW + 2, y);
        });
        // Header bottom border
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y + 2, pageW - margin, y + 2);
        y += rowH;
      };

      drawHeader();

      // Rows
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      rows.forEach((row, idx) => {
        // New page if needed
        if (y + rowH > pageH - 14) {
          doc.addPage();
          y = 16;
          drawHeader();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(9);
        }

        // Alternating row bg
        if (idx % 2 === 1) {
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, y - rowH + 2, contentW, rowH, 'F');
        }

        doc.setTextColor(30, 30, 30);
        selectedCols.forEach((c, i) => {
          const raw = String((row as any)[c] ?? '—');
          // Truncate text to fit column
          const maxChars = Math.floor(colW / 2.1);
          const text = raw.length > maxChars ? raw.slice(0, maxChars - 1) + '…' : raw;
          doc.text(text, margin + i * colW + 2, y);
        });

        // Row divider
        doc.setDrawColor(244, 244, 245);
        doc.line(margin, y + 2, pageW - margin, y + 2);
        y += rowH;
      });

      // Footer on last page
      doc.setFontSize(7.5);
      doc.setTextColor(160, 160, 160);
      doc.text('This document contains PII. Handle securely and do not share without authorisation.', margin, pageH - 6);

      doc.save(`gympe-members-${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success('Member list downloaded');
    } catch (err) {
      console.error(err);
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  };

  const hasActiveFilters = statusFilter !== 'all' || branchId || expiryFrom || expiryTo;

  return (
    <>
      {/* PII Warning Modal */}
      {showPiiWarning && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[2000] p-4 animate-in fade-in duration-200" {...backdropClose(() => setShowPiiWarning(false))}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-in zoom-in duration-300" onClick={e => e.stopPropagation()}>
            <div className="p-8">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center mx-auto mb-5 border border-amber-200">
                <AlertTriangle className="w-8 h-8 text-amber-500" />
              </div>
              <h3 className="text-xl font-black text-center text-zinc-900 mb-2">Contains Personal Data</h3>
              <p className="text-zinc-500 text-sm text-center leading-relaxed mb-6">
                This file includes <span className="font-bold text-zinc-700">personally identifiable information (PII)</span> — names, phone numbers, and other member details.
                <br /><br />
                Please handle it responsibly: store securely, don't share without consent, and delete when no longer needed.
              </p>
              <div className="flex gap-3">
                <button onClick={() => setShowPiiWarning(false)} className="flex-1 py-3 border border-gray-200 rounded-2xl text-sm font-bold text-zinc-600 hover:bg-gray-50 transition-colors flex items-center justify-center gap-2">
                  <X className="w-4 h-4" /> Cancel
                </button>
                <button onClick={generatePdf} disabled={generating} className="flex-1 py-3 bg-zinc-950 text-[#AAD816] rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all flex items-center justify-center gap-2">
                  <FileDown className="w-4 h-4" /> {generating ? 'Generating…' : 'Download'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {/* PII banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 font-medium">
            The downloaded file will contain <strong>personally identifiable information (PII)</strong>. Ensure you have the right to download and store this data, and handle it securely.
          </p>
        </div>

        {/* Filters */}
        <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100 space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Filter className="w-4 h-4 text-zinc-400" />
            <p className="text-xs font-black text-zinc-400 uppercase tracking-wider">Filters</p>
            {hasActiveFilters && (
              <button
                onClick={() => { setStatusFilter('all'); setBranchId(''); setExpiryFrom(''); setExpiryTo(''); }}
                className="ml-auto text-xs font-bold text-rose-500 hover:text-rose-700 transition-colors"
              >
                Clear all
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {/* Status */}
            <div>
              <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Status</label>
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="all">All statuses</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="lead">Lead</option>
                <option value="trial">Trial</option>
              </select>
            </div>

            {/* Branch */}
            <div>
              <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Branch</label>
              <select
                value={branchId}
                onChange={e => setBranchId(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              >
                <option value="">All branches</option>
                {branches.map((b: any) => (
                  <option key={b.id} value={b.id}>{b.displayName || b.name}</option>
                ))}
              </select>
            </div>

            {/* Expiry From */}
            <div>
              <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Plan expiry from</label>
              <input
                type="date"
                value={expiryFrom}
                onChange={e => setExpiryFrom(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>

            {/* Expiry To */}
            <div>
              <label className="block text-[10px] font-bold text-zinc-400 uppercase tracking-wider mb-1.5">Plan expiry to</label>
              <input
                type="date"
                value={expiryTo}
                onChange={e => setExpiryTo(e.target.value)}
                className="w-full px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:ring-2 focus:ring-indigo-200"
              />
            </div>
          </div>
        </div>

        {/* Column picker */}
        <div>
          <p className="text-xs font-black text-zinc-400 uppercase tracking-wider mb-3">Choose columns <span className="text-zinc-300 normal-case font-medium">(max {MAX_COLUMNS})</span></p>
          <div className="flex flex-wrap gap-2">
            {EXPORT_COLUMNS.map(col => {
              const selected = selectedCols.includes(col.key);
              const atMax = !selected && selectedCols.length >= MAX_COLUMNS;
              return (
                <button
                  key={col.key}
                  onClick={() => toggleCol(col.key)}
                  disabled={atMax}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border text-sm font-medium transition-all ${
                    selected
                      ? 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100'
                      : 'bg-white text-zinc-500 border-gray-200 hover:border-gray-300 hover:text-zinc-700'
                  } ${atMax ? 'opacity-40 cursor-not-allowed' : ''}`}
                >
                  {selected && <Check className="w-3 h-3" />}
                  {col.label}
                </button>
              );
            })}
          </div>
          <p className="text-xs text-zinc-400 mt-2">{selectedCols.length}/{MAX_COLUMNS} columns selected</p>
        </div>

        {/* Preview table */}
        {isLoading || isFetching ? (
          <div className="flex justify-center py-8"><LoadingSpinner /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-10 text-zinc-400">
            <Download className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="font-semibold text-sm">No members match the selected filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto rounded-2xl border border-gray-100 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    {selectedCols.length === 0
                      ? <th className="px-4 py-3 text-xs text-zinc-400">Select columns above</th>
                      : selectedCols.map(c => (
                          <th key={c} className="text-left px-4 py-3 text-[10px] font-black text-zinc-400 uppercase tracking-wider">{colLabel(c)}</th>
                        ))
                    }
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {rows.slice(0, 8).map((row: any, i: number) => (
                    <tr key={row.id + i} className="hover:bg-gray-50/50">
                      {selectedCols.map(c => (
                        <td key={c} className="px-4 py-3 text-zinc-700 font-medium">{row[c] ?? '—'}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length > 8 && (
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 text-xs text-zinc-400 font-medium">
                  Showing 8 of {rows.length} members — PDF will include all
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <button onClick={() => refetch()} className="flex items-center gap-2 text-xs font-bold text-zinc-400 hover:text-zinc-600 transition-colors">
                <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} /> Refresh
              </button>
              <button
                onClick={() => setShowPiiWarning(true)}
                disabled={generating || rows.length === 0 || selectedCols.length === 0}
                className="flex items-center gap-2 px-6 py-3 bg-zinc-950 text-[#AAD816] rounded-2xl text-sm font-black uppercase tracking-widest hover:bg-[#AAD816] hover:text-zinc-950 transition-all shadow-xl shadow-zinc-200 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileDown className="w-4 h-4" />
                Download {rows.length} Members as PDF
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Sale Report ──────────────────────────────────────────────────────────────

type SalesGroupBy = 'invoice' | 'lineitem';
type SalesRow = {
  date: string;
  invoiceId: string;
  member: string;
  item: string;
  itemType?: string;
  quantity?: number;
  itemCount?: number;
  totalAmount: number;
};

const ITEM_TYPE_LABEL: Record<string, string> = {
  membership: 'Plan',
  service: 'Service',
  product: 'Product',
};

function todayIso() {
  return new Date().toISOString().split('T')[0];
}
function isoDaysAgo(n: number) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().split('T')[0];
}

function SaleReport() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState<SalesGroupBy>('invoice');
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);

  const spanDays = useMemo(() => {
    const f = new Date(from).getTime();
    const t = new Date(to).getTime();
    if (isNaN(f) || isNaN(t) || t < f) return 0;
    return Math.ceil((t - f) / (1000 * 60 * 60 * 24)) + 1;
  }, [from, to]);

  const rangeError = useMemo(() => {
    if (!from || !to) return 'Pick a from and to date.';
    if (new Date(to) < new Date(from)) return '"To" must be on or after "from".';
    if (spanDays > 90) return 'Date range cannot exceed 90 days.';
    return null;
  }, [from, to, spanDays]);

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['reports-sales', userContext?.user?.id, from, to, groupBy],
    async () => {
      const r = await fetchWithAuth(`/api/reports/sales-report?from=${from}&to=${to}&groupBy=${groupBy}`);
      return r as { data: SalesRow[]; meta: { from: string; to: string; groupBy: SalesGroupBy; total: number; grandTotal: number } };
    },
    { enabled: !!userContext && !userLoading && !rangeError, staleTime: 60 * 1000 }
  );

  const rows = data?.data || [];
  const grandTotal = data?.meta?.grandTotal || 0;

  const exportToExcel = () => {
    if (rows.length === 0) {
      toast.error('No rows to export');
      return;
    }
    setExporting('excel');
    try {
      const headers = groupBy === 'invoice'
        ? ['Date', 'Invoice', 'Member', 'Items', 'Item Count', 'Price (incl. GST)']
        : ['Date', 'Invoice', 'Member', 'Type', 'Item', 'Qty', 'Price (incl. GST)'];

      const escape = (v: any) => {
        const s = String(v ?? '');
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const lines = [headers.join(',')];
      rows.forEach(r => {
        const cols = groupBy === 'invoice'
          ? [r.date, r.invoiceId, r.member, r.item, r.itemCount ?? '', r.totalAmount.toFixed(2)]
          : [r.date, r.invoiceId, r.member, ITEM_TYPE_LABEL[r.itemType || ''] || r.itemType, r.item, r.quantity ?? '', r.totalAmount.toFixed(2)];
        lines.push(cols.map(escape).join(','));
      });
      lines.push('');
      const totalCols = groupBy === 'invoice'
        ? ['Grand Total', '', '', '', '', grandTotal.toFixed(2)]
        : ['Grand Total', '', '', '', '', '', grandTotal.toFixed(2)];
      lines.push(totalCols.map(escape).join(','));

      const csv = '﻿' + lines.join('\n'); // BOM for Excel UTF-8
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sale-report_${from}_to_${to}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Exported');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  const exportToPdf = async () => {
    if (rows.length === 0) {
      toast.error('No rows to export');
      return;
    }
    setExporting('pdf');
    try {
      const { default: jsPDF } = await import('jspdf');
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      const margin = 14;
      const contentW = pageW - margin * 2;

      const cols = groupBy === 'invoice'
        ? [
            { key: 'date', label: 'Date', w: 24 },
            { key: 'invoiceId', label: 'Invoice', w: 38 },
            { key: 'member', label: 'Member', w: 50 },
            { key: 'item', label: 'Items', w: contentW - 24 - 38 - 50 - 36 },
            { key: 'totalAmount', label: 'Price (incl. GST)', w: 36, align: 'right' as const },
          ]
        : [
            { key: 'date', label: 'Date', w: 22 },
            { key: 'invoiceId', label: 'Invoice', w: 32 },
            { key: 'member', label: 'Member', w: 44 },
            { key: 'itemType', label: 'Type', w: 22 },
            { key: 'item', label: 'Item', w: contentW - 22 - 32 - 44 - 22 - 14 - 36 },
            { key: 'quantity', label: 'Qty', w: 14, align: 'right' as const },
            { key: 'totalAmount', label: 'Price (incl. GST)', w: 36, align: 'right' as const },
          ];

      const rowH = 7;
      const headerH = 8;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(16);
      doc.setTextColor(17, 17, 17);
      doc.text('Sale Report', margin, 16);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`${from} to ${to}  -  ${rows.length} ${groupBy === 'invoice' ? 'invoices' : 'line items'}  -  Grand total Rs. ${grandTotal.toLocaleString('en-IN')}`, margin, 22);

      const fitText = (text: string, maxW: number) => {
        if (doc.getTextWidth(text) <= maxW) return text;
        let truncated = text;
        while (truncated.length > 1 && doc.getTextWidth(truncated + '…') > maxW) {
          truncated = truncated.slice(0, -1);
        }
        return truncated + '…';
      };

      doc.setDrawColor(228, 228, 231);
      doc.line(margin, 25, pageW - margin, 25);

      let y = 33;

      const drawHeader = () => {
        doc.setFillColor(244, 244, 245);
        doc.rect(margin, y - headerH + 2, contentW, headerH, 'F');
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        doc.setTextColor(80, 80, 80);
        let x = margin;
        cols.forEach(c => {
          const tx = c.align === 'right' ? x + c.w - 2 : x + 2;
          doc.text(c.label.toUpperCase(), tx, y, { align: c.align === 'right' ? 'right' : 'left' });
          x += c.w;
        });
        doc.setDrawColor(200, 200, 200);
        doc.line(margin, y + 2, pageW - margin, y + 2);
        y += rowH;
      };

      drawHeader();
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(40, 40, 40);

      rows.forEach((row, idx) => {
        if (y + rowH > pageH - 14) {
          doc.addPage();
          y = 16;
          drawHeader();
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(8.5);
          doc.setTextColor(40, 40, 40);
        }
        if (idx % 2 === 1) {
          doc.setFillColor(250, 250, 250);
          doc.rect(margin, y - rowH + 2, contentW, rowH, 'F');
        }
        let x = margin;
        cols.forEach(c => {
          let value: string;
          if (c.key === 'totalAmount') {
            value = `Rs. ${row.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          } else if (c.key === 'itemType') {
            value = ITEM_TYPE_LABEL[row.itemType || ''] || row.itemType || '-';
          } else if (c.key === 'quantity') {
            value = String(row.quantity ?? '');
          } else {
            value = String((row as any)[c.key] ?? '-');
          }
          value = fitText(value, c.w - 4);
          const tx = c.align === 'right' ? x + c.w - 2 : x + 2;
          doc.text(value, tx, y - 1, { align: c.align === 'right' ? 'right' : 'left' });
          x += c.w;
        });
        y += rowH;
      });

      // Grand total row
      if (y + rowH > pageH - 14) { doc.addPage(); y = 16; }
      doc.setDrawColor(200, 200, 200);
      doc.line(margin, y - rowH + 3, pageW - margin, y - rowH + 3);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(17, 17, 17);
      doc.text('Grand Total', margin + 2, y);
      doc.text(
        `Rs. ${grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
        pageW - margin - 2,
        y,
        { align: 'right' }
      );

      doc.save(`sale-report_${from}_to_${to}.pdf`);
      toast.success('Exported');
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">From</label>
            <input
              type="date"
              value={from}
              max={to}
              onChange={e => setFrom(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zinc-950/10"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">To</label>
            <input
              type="date"
              value={to}
              min={from}
              max={todayIso()}
              onChange={e => setTo(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zinc-950/10"
            />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Group by</label>
            <div className="inline-flex bg-zinc-100 rounded-xl p-0.5">
              <button
                onClick={() => setGroupBy('invoice')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${groupBy === 'invoice' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500'}`}
              >
                Invoice
              </button>
              <button
                onClick={() => setGroupBy('lineitem')}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${groupBy === 'lineitem' ? 'bg-white shadow-sm text-zinc-900' : 'text-zinc-500'}`}
              >
                Line Item
              </button>
            </div>
          </div>
          <button
            onClick={() => refetch()}
            disabled={!!rangeError || isFetching}
            className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>

        <div className="flex items-end gap-2">
          <button
            onClick={exportToPdf}
            disabled={!!rangeError || rows.length === 0 || !!exporting}
            className="px-4 py-2 rounded-xl bg-zinc-950 text-[#AAD816] text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <FileDown className="w-3.5 h-3.5" />
            {exporting === 'pdf' ? 'Generating…' : 'Export PDF'}
          </button>
          <button
            onClick={exportToExcel}
            disabled={!!rangeError || rows.length === 0 || !!exporting}
            className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" />
            {exporting === 'excel' ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </div>

      {rangeError && (
        <div className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          {rangeError}
        </div>
      )}

      {!rangeError && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-zinc-500 font-medium">
            <span className="font-bold text-zinc-800">{rows.length}</span> {groupBy === 'invoice' ? 'invoice' : 'line item'}{rows.length !== 1 ? 's' : ''}
            <span className="text-zinc-400"> · {spanDays} day{spanDays !== 1 ? 's' : ''}</span>
          </p>
          <p className="text-zinc-700 font-bold">
            Grand total: ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 flex justify-center"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-zinc-400 text-sm font-medium">No sales in this date range.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-gray-100">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Date</th>
                <th className="text-left px-4 py-3">Invoice</th>
                <th className="text-left px-4 py-3">Member</th>
                {groupBy === 'lineitem' && <th className="text-left px-4 py-3">Type</th>}
                <th className="text-left px-4 py-3">Item</th>
                {groupBy === 'lineitem' && <th className="text-right px-4 py-3">Qty</th>}
                <th className="text-right px-4 py-3">Price (incl. GST)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r, idx) => (
                <tr key={`${r.invoiceId}-${idx}`} className="hover:bg-zinc-50/50">
                  <td className="px-4 py-3 font-mono text-xs text-zinc-600">{r.date}</td>
                  <td className="px-4 py-3 font-mono text-xs text-zinc-700">{r.invoiceId}</td>
                  <td className="px-4 py-3 font-medium text-zinc-800">{r.member}</td>
                  {groupBy === 'lineitem' && (
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border bg-indigo-50 text-indigo-700 border-indigo-200">
                        {ITEM_TYPE_LABEL[r.itemType || ''] || r.itemType}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-zinc-700">
                    {r.item}
                    {groupBy === 'invoice' && r.itemCount && r.itemCount > 1 && (
                      <span className="ml-1 text-[10px] font-bold text-zinc-400">({r.itemCount} items)</span>
                    )}
                  </td>
                  {groupBy === 'lineitem' && <td className="px-4 py-3 text-right font-mono text-xs text-zinc-600">{r.quantity}</td>}
                  <td className="px-4 py-3 text-right font-mono font-bold text-zinc-900">
                    ₹{r.totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Trainer Engagement Report ────────────────────────────────────────────────

type TrainerEngagementMember = {
  memberId: string;
  memberName: string;
  phoneNumber: string;
  engagementDays: number;
  engagementPercent: number;
  checkIns: number;
  assignmentCount: number;
  firstAssignedAt: string;
  currentlyAssigned: boolean;
  eligible: boolean;
};

type TrainerEngagementRow = {
  trainerId: string;
  trainerName: string;
  memberCount: number;
  eligibleMemberCount: number;
  totalEngagementDays: number;
  totalCheckIns: number;
  members: TrainerEngagementMember[];
};

function TrainerEngagementReport() {
  const { fetchWithAuth } = useApi();
  const { userContext, loading: userLoading } = useUserContext();
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [trainerId, setTrainerId] = useState('');
  const [trainers, setTrainers] = useState<Array<{ userId: string; name: string }>>([]);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const minFrom = useMemo(() => isoDaysAgo(90), []);

  const spanDays = useMemo(() => {
    const f = new Date(from).getTime();
    const t = new Date(to).getTime();
    if (isNaN(f) || isNaN(t) || t < f) return 0;
    return Math.ceil((t - f) / (1000 * 60 * 60 * 24)) + 1;
  }, [from, to]);

  const rangeError = useMemo(() => {
    if (!from || !to) return 'Pick a from and to date.';
    if (new Date(to) < new Date(from)) return '"To" must be on or after "from".';
    if (new Date(from) < new Date(minFrom)) return '"From" cannot be more than 90 days ago.';
    if (new Date(to) > new Date(todayIso())) return '"To" cannot be in the future.';
    if (spanDays > 90) return 'Date range cannot exceed 90 days.';
    return null;
  }, [from, to, spanDays, minFrom]);

  // Load trainers once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const json = await fetchWithAuth('/api/trainers?role=trainer&status=active') as { data?: any[] };
        if (cancelled) return;
        const list: Array<{ userId: string; name: string }> = (json.data || [])
          .map((t: any) => ({ userId: t.userId, name: t.displayName || t.name || '—' }))
          .filter(t => !!t.userId);
        const seen = new Set<string>();
        setTrainers(list.filter(t => seen.has(t.userId) ? false : (seen.add(t.userId), true)));
      } catch {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const { data, isLoading, refetch, isFetching } = useQuery(
    ['reports-trainer', userContext?.user?.id, from, to, trainerId],
    async () => {
      const url = `/api/reports/trainer-engagement?from=${from}&to=${to}${trainerId ? `&trainerId=${trainerId}` : ''}`;
      const r = await fetchWithAuth(url);
      return r as { data: TrainerEngagementRow[]; meta: { from: string; to: string; periodDays: number; threshold: number; trainers: number } };
    },
    { enabled: !!userContext && !userLoading && !rangeError, staleTime: 60 * 1000 }
  );

  // Threshold is adjustable here so the decision can be made in-place.
  // Defaults to the gym setting; recomputed client-side from engagementPercent (no refetch).
  const gymThreshold = data?.meta?.threshold ?? 50;
  const [threshold, setThreshold] = useState(50);
  const [thresholdTouched, setThresholdTouched] = useState(false);
  useEffect(() => {
    if (!thresholdTouched && data?.meta?.threshold != null) setThreshold(data.meta.threshold);
  }, [data?.meta?.threshold, thresholdTouched]);

  const rows = useMemo(
    () => (data?.data || []).map(t => {
      const members = t.members.map(m => ({ ...m, eligible: m.engagementPercent >= threshold }));
      return { ...t, members, eligibleMemberCount: members.filter(m => m.eligible).length };
    }),
    [data, threshold]
  );

  const toggle = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">From</label>
          <input
            type="date"
            value={from}
            min={minFrom}
            max={to}
            onChange={e => setFrom(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zinc-950/10"
          />
        </div>
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">To</label>
          <input
            type="date"
            value={to}
            min={from}
            max={todayIso()}
            onChange={e => setTo(e.target.value)}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zinc-950/10"
          />
        </div>
        {trainers.length > 0 && (
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Trainer</label>
            <select
              value={trainerId}
              onChange={e => setTrainerId(e.target.value)}
              className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-zinc-950/10 max-w-[220px]"
            >
              <option value="">All trainers</option>
              {trainers.map(t => (
                <option key={t.userId} value={t.userId}>{t.name}</option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className="block text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1">Eligible ≥ (%)</label>
          <NumberInput
            integer
            min={0}
            max={100}
            emptyValue={0}
            value={threshold}
            onChange={v => {
              setThresholdTouched(true);
              setThreshold(v ?? 0);
            }}
            className="px-3 py-2 rounded-xl border border-gray-200 text-sm font-medium w-24 focus:outline-none focus:ring-2 focus:ring-zinc-950/10"
          />
        </div>
        <button
          onClick={() => refetch()}
          disabled={!!rangeError || isFetching}
          className="px-3 py-2 rounded-xl border border-gray-200 text-xs font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {rangeError && (
        <div className="text-xs font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 inline-flex items-center gap-1.5">
          <AlertTriangle className="w-3.5 h-3.5" />
          {rangeError}
        </div>
      )}

      {!rangeError && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-zinc-500 font-medium">
            <span className="font-bold text-zinc-800">{rows.length}</span> trainer{rows.length !== 1 ? 's' : ''} with engagement
            <span className="text-zinc-400"> · {spanDays} day{spanDays !== 1 ? 's' : ''}</span>
          </p>
          <p className="text-zinc-700 font-bold text-xs">
            Eligibility threshold: <span className="text-amber-600">{threshold}%</span>
            {threshold !== gymThreshold ? (
              <button
                onClick={() => { setThresholdTouched(false); setThreshold(gymThreshold); }}
                className="text-zinc-400 font-normal ml-1 underline hover:text-zinc-600"
              >
                (reset to gym default {gymThreshold}%)
              </button>
            ) : (
              <span className="text-zinc-400 font-normal ml-1">(gym default — adjustable above)</span>
            )}
          </p>
        </div>
      )}

      {isLoading ? (
        <div className="py-16 flex justify-center"><LoadingSpinner /></div>
      ) : rows.length === 0 ? (
        <div className="text-center py-16 text-zinc-400 text-sm font-medium">
          No trainer engagement in this period.
          <p className="text-[11px] mt-2 text-zinc-400">Assign trainers to members from the member edit drawer (Trainers tab).</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(t => {
            const isOpen = expanded.has(t.trainerId);
            return (
              <div key={t.trainerId} className="rounded-2xl border border-gray-100 overflow-hidden bg-white">
                <button
                  onClick={() => toggle(t.trainerId)}
                  className="w-full flex items-center justify-between p-4 hover:bg-zinc-50/50 text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-zinc-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="font-bold text-zinc-900 truncate">{t.trainerName}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">
                        <span className="font-bold text-emerald-600">{t.eligibleMemberCount}</span> eligible
                        <span className="text-zinc-300 mx-1">·</span>
                        <span>{t.memberCount} total</span>
                        <span className="text-zinc-300 mx-1">·</span>
                        <span>{t.totalCheckIns} check-ins</span>
                        <span className="text-zinc-300 mx-1">·</span>
                        <span>{t.totalEngagementDays} engaged days</span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-xl font-black text-zinc-900">{t.eligibleMemberCount}</div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">eligible</div>
                  </div>
                </button>
                {isOpen && (
                  <div className="border-t border-gray-100 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-zinc-50/50 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                        <tr>
                          <th className="text-left px-4 py-2.5">Member</th>
                          <th className="text-left px-4 py-2.5">Phone</th>
                          <th className="text-right px-4 py-2.5">Engagement</th>
                          <th className="text-right px-4 py-2.5">Days</th>
                          <th className="text-right px-4 py-2.5">Check-ins</th>
                          <th className="text-center px-4 py-2.5">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {t.members.map(m => (
                          <tr key={m.memberId} className="hover:bg-zinc-50/30">
                            <td className="px-4 py-2.5 font-medium text-zinc-800">
                              {m.memberName}
                              {!m.currentlyAssigned && (
                                <span className="ml-2 text-[10px] font-bold text-zinc-400 uppercase">past</span>
                              )}
                            </td>
                            <td className="px-4 py-2.5 font-mono text-xs text-zinc-500">{m.phoneNumber}</td>
                            <td className="px-4 py-2.5 text-right">
                              <span className={`font-mono font-bold ${m.eligible ? 'text-emerald-700' : 'text-zinc-400'}`}>
                                {m.engagementPercent.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-600">{m.engagementDays}</td>
                            <td className="px-4 py-2.5 text-right font-mono text-xs text-zinc-600">{m.checkIns}</td>
                            <td className="px-4 py-2.5 text-center">
                              {m.eligible ? (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border bg-emerald-50 text-emerald-700 border-emerald-200">Eligible</span>
                              ) : (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border bg-gray-50 text-gray-500 border-gray-200">Below {threshold}%</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const [activeTab, setActiveTab] = useState<Tab>('expiry');

  return (
    <div className="space-y-8 pb-12 animate-in fade-in duration-700">
      {/* Header */}
      <div>
        <h1 className="text-4xl font-black tracking-tighter">
          <span className="ai-gradient-text">REPORTS</span>
        </h1>
        <p className="text-zinc-500 font-bold uppercase tracking-widest text-[10px] mt-2">
          Business Intelligence & Member Insights
        </p>
      </div>

      {/* Tab Nav */}
      <div className="flex flex-wrap gap-2">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold transition-all ${
              activeTab === tab.id
                ? 'bg-zinc-950 text-[#AAD816] shadow-lg shadow-zinc-200'
                : 'bg-white text-zinc-500 border border-gray-200 hover:border-gray-300 hover:text-zinc-700'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab description */}
      <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-6 md:p-8">
        <div className="mb-6 pb-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-black text-zinc-900">{TABS.find(t => t.id === activeTab)?.label}</h2>
            <p className="text-xs text-zinc-400 font-medium mt-0.5">{TABS.find(t => t.id === activeTab)?.description}</p>
          </div>
          <div className={`p-3 rounded-2xl ${
            activeTab === 'expiry' ? 'bg-amber-50' :
            activeTab === 'inactive' ? 'bg-rose-50' :
            activeTab === 'revenue' ? 'bg-emerald-50' :
            activeTab === 'sales' ? 'bg-sky-50' :
            activeTab === 'trainer' ? 'bg-violet-50' :
            'bg-indigo-50'
          }`}>
            {TABS.find(t => t.id === activeTab)?.icon && (
              <span className={`[&>svg]:w-5 [&>svg]:h-5 ${
                activeTab === 'expiry' ? '[&>svg]:text-amber-500' :
                activeTab === 'inactive' ? '[&>svg]:text-rose-500' :
                activeTab === 'revenue' ? '[&>svg]:text-emerald-600' :
                activeTab === 'sales' ? '[&>svg]:text-sky-600' :
                activeTab === 'trainer' ? '[&>svg]:text-violet-600' :
                '[&>svg]:text-indigo-600'
              }`}>
                {TABS.find(t => t.id === activeTab)?.icon}
              </span>
            )}
          </div>
        </div>

        {activeTab === 'expiry' && <ExpiryForecast />}
        {activeTab === 'inactive' && <InactiveMembers />}
        {activeTab === 'revenue' && <RevenueCollection />}
        {activeTab === 'sales' && <SaleReport />}
        {activeTab === 'trainer' && <TrainerEngagementReport />}
        {activeTab === 'download' && <DownloadMembers />}
      </div>
    </div>
  );
}
